import { useDepositPaymentMethods } from '@/feature/deposit/hooks/use-deposit-payment-methods';
import { usePaymentFlags } from '@/feature/devsettings/hooks/use-payment-flags-store';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';

/**
 * Custom hook that returns the availability of purchase/redemption with fantasy money.
 * Fantasy money transactions are only eligible for products with a social wallet (Sweeps and Casino).
 *
 * @returns {Object} Object containing fantasy money feature flags
 * @returns {boolean} isFantasyMoneyPurchaseEnabled - True if fantasy money purchases should be enabled
 * @returns {boolean} isFantasyMoneyRedemptionEnabled - True if fantasy money redemption should be enabled
 */
export const useFantasyMoneyFlags = () => {
    const hasSocialWallet = useHasSocialWallet();
    const { data: depositPaymentMethods } = useDepositPaymentMethods();
    const { flags } = usePaymentFlags();
    const jurisdictionSettings = useJurisdictionStore(state => state.jurisdictionSettings);

    const isDebugMode = flags.isDebugMode.enabled;

    // Fantasy money flags with wallet balance logic
    const isFantasyMoneyPurchaseEnabledRaw = isDebugMode
        ? flags.enableFantasyMoneyPurchase.enabled
        : jurisdictionSettings?.productConfig?.settings?.enable_fantasy_money_purchase ?? false;

    const isFantasyMoneyRedemptionEnabledRaw = isDebugMode
        ? flags.enableFantasyMoneyRedemption.enabled
        : jurisdictionSettings?.productConfig?.settings?.enable_fantasy_money_redemption ?? false;

    // Apply social wallet and wallet balance checks for fantasy money
    let isFantasyMoneyPurchaseEnabled = false;
    let isFantasyMoneyRedemptionEnabled = false;

    if (hasSocialWallet) {
        const fantasyWallet = depositPaymentMethods?.fantasy_wallet;
        const fantasyWalletBalance = parseFloat(fantasyWallet?.real_amount || '0');

        isFantasyMoneyPurchaseEnabled = fantasyWalletBalance > 0 && isFantasyMoneyPurchaseEnabledRaw;
        isFantasyMoneyRedemptionEnabled = isFantasyMoneyRedemptionEnabledRaw;
    }

    return {
        isFantasyMoneyPurchaseEnabled,
        isFantasyMoneyRedemptionEnabled,
    };
};
